import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import _ from 'lodash';

import { AppDispatch } from 'app/store';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';

import {
  getRequiredDocuments,
  getVesselCertificates,
  selectDocuments,
  selectShipCertificatesByTitle,
} from '../../../slices/documentsSlice';
import {
  createPortcallStatusLog,
  getPortcallStatusLog,
  getVessel,
  VesselData,
} from '../../../slices/portCallSlice';

import Page from '../../../../layout/components/Page';
import PageHeader from '../../../../layout/components/PageHeader';
import { CERTIFICATES } from '../../documents/constants';
import UploadDownloadBox from '../../local-formalities/components/UploadDownloadBox';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';

const ShipCertificates = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const params = useParams();
  const [submitEnabled, setSubmitEnabled] = useState(true);

  const requiredDocuments = useSelector(selectDocuments);
  const shipCertificatesByTitle = useSelector(selectShipCertificatesByTitle);

  const documents = useMemo(
    () => requiredDocuments?.documents?.requestedCertificates ?? [],
    [requiredDocuments?.documents?.requestedCertificates]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (params.uid) {
        const response = await dispatch(getVessel(params.uid));
        const vesselData = response.payload as VesselData;

        await Promise.all([
          dispatch(getVesselCertificates({ vesselUid: vesselData.uid })),
          dispatch(getRequiredDocuments({ uid: params.uid })),
        ]);
      }
    };

    fetchData();
  }, [dispatch, params.uid]);

  useEffect(() => {
    setSubmitEnabled(true);
    documents.forEach((document) => {
      if (!document?.documentUid) {
        setSubmitEnabled(false);
      }
    });
  }, [documents]);

  const onSubmitClick = async () => {
    if (params.uid) {
      const isSuccessUpdateStatus = await dispatch(
        createPortcallStatusLog({
          statusLog: {
            uid: params.uid,
            section: DossierSectionOptions.ShipCertificates,
            status: DossierSectionStatusOptions.SubmittedToNmap,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccessUpdateStatus) return;

      dispatch(getPortcallStatusLog(params.uid));
    }
    navigate(`/${params.uid}/summary`);
  };

  const renderBody = () => {
    return _.isEmpty(shipCertificatesByTitle)
      ? renderEmptyCertificates()
      : renderCertificates();
  };

  const renderCertificates = () => {
    return (
      <>
        <PageHeader title={'Ship Certificates'} />
        {Object.keys(shipCertificatesByTitle).map((title, i) => {
          return (
            <div key={`doc-category-${i}`}>
              <h3>{title}</h3>
              <p>Please provide the requested ship certificates:</p>
              <div className="documents-list">
                {shipCertificatesByTitle[title].map((document) => {
                  const isMandatory =
                    document?.documentType?.id !== CERTIFICATES.ISO_14001.id;
                  return (
                    <>
                      <UploadDownloadBox
                        key={`doc-${document.documentType.id}`}
                        category="requestedCertificates"
                        document={document}
                        isMandatory={isMandatory}
                        isShipCertificate={true}>
                        {document.documentType.otherName ||
                          document.documentType.name}
                      </UploadDownloadBox>
                      <div style={{ margin: '40px 0' }}></div>
                    </>
                  );
                })}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderEmptyCertificates = () => {
    return (
      <PageHeader
        title={'Ship Certificates'}
        subtitle={'No certificates requested.'}
      />
    );
  };

  return (
    <Page>
      <div className="safety-documents">
        {renderBody()}
        <SkipAndNextButtons
          onSubmitClick={onSubmitClick}
          next={`/${params.uid}/summary`}
          back={`/${params.uid}/local-formalities`}
          submitLabel="Submit"
          submitDisabled={!submitEnabled}
        />
      </div>
    </Page>
  );
};

export default ShipCertificates;
